import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ultraEligiblePetsQuery } from '../graphql-operations';
import { AppState, gqlTypes } from '../types';

export interface UltraEligiblePet {
  petId: string;
  name: string;
  moduleId: string;
  photoUrl: string | null;
}

export interface UltraEligiblePetsResult {
  pets: UltraEligiblePet[];
  loading: boolean;
}

// Locally-typed shape of the fields we read from ultraEligiblePetsQuery — declared inline (rather than via the
// generated gqlTypes.userPets) so we can read the newly-added device model + access type without a
// codegen round-trip, mirroring the free-trial eligibility hooks.
interface UltraUserPets {
  currentUser?: {
    userHouseholds?: Array<{
      householdRole: gqlTypes.UserHouseholdRole;
      household: {
        pets: Array<{
          id: string;
          name: string;
          photos: { first?: { image: { fullSize: string } } | null };
          device?: {
            moduleId: string;
            currentCollarModel?: string | null;
            subscriptionAccessType?: gqlTypes.SubscriptionAccessType | null;
          } | null;
        }>;
      };
    }>;
  } | null;
}

// Collar models whose active membership an Ultra can be added to under one membership (hardware only):
// the whole Series 3 family (S3, S3.1, S3+, S3+ Int) and Mini (Mini, Mini Int). `currentCollarModel`
// is the consumer model string, so match by family prefix. Excludes legacy S1/S2/S2.5 and Ultra itself.
const isEligibleCollarModel = (model?: string | null): boolean => {
  const m = model?.toLowerCase() ?? '';
  return m.startsWith('s3') || m.startsWith('mini');
};

/**
 * The signed-in shopper's pets that already have an active S3, S3+, or Mini membership — i.e. the dogs
 * an Ultra can be added to under their existing membership (hardware-only). Pets without a qualifying
 * active membership are excluded, which the PDP uses to hide the "device only" option and default to
 * the new-membership flow.
 */
export default function useUltraEligiblePets(): UltraEligiblePetsResult {
  // Only query the signed-in user's pets — the `currentUser` query errors for unauthenticated
  // visitors, which would otherwise blank the signed-out PDP.
  const loggedIn = useSelector((state: AppState) => !!state.session);
  const { data, loading } = useQuery<UltraUserPets>(ultraEligiblePetsQuery, { skip: !loggedIn });

  const pets = useMemo<UltraEligiblePet[]>(() => {
    const households = data?.currentUser?.userHouseholds ?? [];
    return (
      households
        // Only the household owner can add an Ultra under their existing membership. Caretakers and
        // provisional caretakers have restricted access to the household's pets and must not see them here.
        .filter((h) => h.householdRole === gqlTypes.UserHouseholdRole.OWNER)
        .flatMap((h) =>
          h.household.pets
            .filter(
              (p) =>
                p.device?.moduleId &&
                p.device.subscriptionAccessType === gqlTypes.SubscriptionAccessType.FULL_ACCESS &&
                isEligibleCollarModel(p.device.currentCollarModel),
            )
            .map((p) => ({
              petId: p.id,
              name: p.name,
              moduleId: p.device!.moduleId,
              photoUrl: p.photos.first?.image.fullSize ?? null,
            })),
        )
    );
  }, [data]);

  return { pets, loading };
}
